import type {
  Chart as ChartJSChart,
  ChartConfiguration,
  ChartData,
  ChartDataset,
  ChartType,
  DefaultDataPoint,
} from "chart.js";
import { forwardRef, useCallback, useRef } from "react";
import Loadable from "react-loadable";

import Spinner from "~/components/shared/Spinner";

type LoadingProps = {
  error?: unknown;
  pastDelay?: boolean;
};

const LoadingMessage = (props: LoadingProps) => {
  if (props.error) {
    return <div>Error loading charts. Please reload</div>;
  } else if (props.pastDelay) {
    return (
      <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  return null;
};

const LoadableChart = Loadable({
  loader: () => import("./chart").then((module) => module.default),
  loading: LoadingMessage,
});

export type ChartProps = {
  chartOptions: ChartConfiguration<any, any> & {
    dangerouslyEvaluateFunctions?: boolean;
  };
  canvasId?: string;
};

export type ForwardedChartHandle = {
  getChartInstance: () => ChartJSChart | undefined;
};

export const ForwardedChart = forwardRef<ForwardedChartHandle, ChartProps>((props, ref) => (
  <LoadableChart {...props} forwardedRef={ref} />
));

type Props = ChartProps & {
  height: number;
  width?: number;
};

export default function Chart(props: Props) {
  return (
    <div className="relative" style={{ height: props.height, width: props.width }}>
      <LoadableChart chartOptions={props.chartOptions} canvasId={props.canvasId} />
    </div>
  );
}

/**
 * Hook for hooking into the ChartJS instance, allowing for data updates
 * when paired with the `ForwardedChart` component.
 *
 * Example usage:
 *   const { chartRef, updateData } = useChart();
 *
 *   useEffect(() => {
 *    updateData(data);
 *   }, [data]);
 *
 *   return <ForwardedChart ref={chartRef} chartOptions={chartOptions} />;
 */
export function useChart<Type extends ChartType = ChartType, DataPoint = DefaultDataPoint<Type>>() {
  const chartRef = useRef<ForwardedChartHandle>(null);

  const updateData = useCallback((data: ChartData<Type, DataPoint[]>) => {
    const instance = chartRef.current?.getChartInstance();
    if (!instance) {
      return;
    }

    const currentDatasets: any[] = instance.data?.datasets || [];

    // Preserve the visibility and colours of the existing datasets
    const datasets: ChartDataset[] = data.datasets.map((dataset, index) => {
      return {
        ...currentDatasets[index],
        ...dataset,
        hidden: !instance.isDatasetVisible(index),
      };
    });

    instance.data = {
      labels: data.labels,
      datasets,
    };
    instance.update();
  }, []);

  return { chartRef, updateData };
}
